'use client';

import { ReactNode, useMemo } from 'react';

import { useLocale } from 'next-intl';

import { useConfig } from '@/components/providers/config-provider';
import {
  buildHotelCardDarkOverrides,
  HOTEL_CARD_CSS_VAR_MAP,
  buildCarouselButtonVars,
  TenantBranding,
  type TenantHotelCardStyle,
} from '@/lib/types/tenant';
import { getFontForLocale, getFontUrls, getLocalFontFaces } from '@/lib/utils/brand-fonts';
import { hexToRgba, hexToRgbChannels } from '@/lib/utils/color';

const BRANDING_STYLE_ID = 'bazak-branding-styles';

const PORTAL_SELECTORS = [
  '[data-slot="tooltip-content"]',
  '[data-slot="dialog-portal"]',
  '[data-slot="sheet-portal"]',
  '[data-radix-popper-content-wrapper]',
];

const BRANDING_SELECTORS = ['.bazak-branding', ...PORTAL_SELECTORS];

// Elements that should keep the default Geist font instead of branded font
const DEFAULT_FONT_SELECTORS = [
  '.aui-reasoning-root',
  '.aui-tool-debug-info',
  '.aui-dev-tools-button',
];

function generateCssVariables(branding: TenantBranding, locale: string): Record<string, string> {
  const vars: Record<string, string> = {};
  const { colors, fonts, components } = branding;

  if (colors?.primary) {
    const { primary, primaryForeground = 'oklch(0.985 0 0)' } = colors;
    const textColor = colors.foreground ?? primary;

    Object.assign(vars, {
      '--primary': primary,
      '--color-primary': primary,
      '--primary-foreground': primaryForeground,
      '--color-primary-foreground': primaryForeground,
      '--foreground': textColor,
      '--color-foreground': textColor,
      '--card-foreground': textColor,
      '--color-card-foreground': textColor,
      '--popover-foreground': textColor,
      '--color-popover-foreground': textColor,
      '--accent-foreground': textColor,
      '--color-accent-foreground': textColor,
      '--secondary-foreground': textColor,
      '--color-secondary-foreground': textColor,
      '--sidebar-primary': primary,
      '--sidebar-primary-foreground': primaryForeground,
      '--sidebar-foreground': textColor,
      '--sidebar-accent-foreground': textColor,
      '--sidebar-border': hexToRgba(textColor, 0.21),
      '--color-sidebar-border': hexToRgba(textColor, 0.21),
      '--border': hexToRgba(textColor, 0.21),
      '--color-border': hexToRgba(textColor, 0.21),
      '--muted-foreground': hexToRgba(textColor, 0.7),
      '--color-muted-foreground': hexToRgba(textColor, 0.7),
    });
    // Only a tenant that separates its text color from its brand color gets text-colored trail
    // icons; everyone else keeps them in the brand color.
    if (colors.foreground) vars['--trail-icon-color'] = 'var(--foreground)';
  }

  if (colors?.accent) {
    const { accent, accentForeground = 'oklch(0.985 0 0)', applyAccentToUI } = colors;
    Object.assign(vars, {
      '--brand-accent': colors.brandAccent || accent,
      '--brand-accent-foreground': colors.brandAccentForeground || accentForeground,
    });
    if (applyAccentToUI) {
      Object.assign(vars, {
        '--accent': accent,
        '--color-accent': accent,
        '--secondary': accent,
        '--color-secondary': accent,
      });
    }
  }

  const SEMANTIC_COLORS = ['warning', 'info', 'destructive'] as const;
  for (const name of SEMANTIC_COLORS) {
    const value = colors?.[name];
    if (value) {
      vars[`--${name}`] = value;
      vars[`--${name}-rgb`] = hexToRgbChannels(value);
    }
  }

  // Font variable - simple locale-based lookup
  if (fonts) {
    const fontName = getFontForLocale(fonts, locale);
    if (fontName) {
      const fontFamily = fontName
        .split(',')
        .map(f => `"${f.trim()}"`)
        .join(', ');
      vars['--font-brand'] = `${fontFamily}, sans-serif`;
    }
  }

  Object.assign(vars, buildCarouselButtonVars(branding));

  const welcome = components?.welcome;
  if (welcome?.subtitle?.color) {
    vars['--welcome-subtitle-color'] = welcome.subtitle.color;
  }
  if (welcome?.title?.fontSize) {
    vars['--welcome-title-font-size'] = `${welcome.title.fontSize}px`;
  }
  if (welcome?.title?.fontWeight) {
    vars['--welcome-title-font-weight'] = `${welcome.title.fontWeight}`;
  }
  if (welcome?.subtitle?.fontSize) {
    vars['--welcome-subtitle-font-size'] = `${welcome.subtitle.fontSize}px`;
  }
  if (welcome?.subtitle?.fontWeight) {
    vars['--welcome-subtitle-font-weight'] = `${welcome.subtitle.fontWeight}`;
  }

  if (components?.composerSend) {
    const { background, foreground } = components.composerSend;
    if (background) vars['--composer-send-bg'] = background;
    if (foreground) vars['--composer-send-foreground'] = foreground;
  }

  if (components?.hotelCard) {
    const hc = components.hotelCard;
    for (const [prop, cssVar] of Object.entries(HOTEL_CARD_CSS_VAR_MAP)) {
      const val = hc[prop as keyof TenantHotelCardStyle];
      if (val) vars[cssVar] = val;
    }
  }

  return vars;
}

function generateDarkModeOverrides(branding: TenantBranding): string {
  const fg = branding.colors?.dark?.foreground ?? '#f1f5f9';
  const mutedFg = branding.colors?.dark?.mutedForeground ?? 'rgba(255, 255, 255, 0.5)';
  const border = branding.colors?.dark?.border ?? '#333';

  const vars: Record<string, string> = {
    '--primary': fg,
    '--color-primary': fg,
    '--primary-foreground': '#000',
    '--color-primary-foreground': '#000',
    '--foreground': fg,
    '--color-foreground': fg,
    '--card-foreground': fg,
    '--color-card-foreground': fg,
    '--popover-foreground': fg,
    '--color-popover-foreground': fg,
    '--accent-foreground': fg,
    '--color-accent-foreground': fg,
    '--secondary-foreground': fg,
    '--color-secondary-foreground': fg,
    '--muted-foreground': mutedFg,
    '--color-muted-foreground': mutedFg,
    '--border': border,
    '--color-border': border,
    '--muted': '#1a1a1a',
    '--color-muted': '#1a1a1a',
    '--accent': '#333',
    '--color-accent': '#333',
    '--secondary': '#333',
    '--color-secondary': '#333',
    '--sidebar-foreground': fg,
    '--sidebar-accent-foreground': fg,
    '--sidebar-border': border,
    '--color-sidebar-border': border,
  };

  Object.assign(vars, buildHotelCardDarkOverrides(branding));

  const varsString = Object.entries(vars)
    .map(([key, value]) => `${key}: ${value}`)
    .join('; ');

  const darkSelectors = BRANDING_SELECTORS.map(s => `.dark ${s}`).join(', ');
  return `${darkSelectors} { ${varsString}; }`;
}

function generateComponentOverrides(branding: TenantBranding): string[] {
  const rules: string[] = [];
  const { components } = branding;

  if (components?.composerSend) {
    const bg = components.composerSend.background;
    const fg = components.composerSend.foreground;
    const props = [
      bg && `background-color: var(--composer-send-bg)`,
      fg && `color: var(--composer-send-foreground)`,
    ]
      .filter(Boolean)
      .join('; ');
    if (props) {
      rules.push(`.aui-composer-send, .aui-composer-cancel { ${props}; }`);
      rules.push(`.aui-composer-send:hover, .aui-composer-cancel:hover { opacity: 0.9; }`);
    }
  }

  return rules;
}

interface BrandingProviderProps {
  children: ReactNode;
}

export function BrandingProvider({ children }: BrandingProviderProps): ReactNode {
  const { branding } = useConfig();
  const locale = useLocale();

  const { cssVariables, fontUrls, localFontFaces } = useMemo(() => {
    if (!branding) {
      return { cssVariables: {} as Record<string, string>, fontUrls: [], localFontFaces: null };
    }

    return {
      cssVariables: generateCssVariables(branding, locale),
      fontUrls: branding.fonts ? getFontUrls(branding.fonts, locale) : [],
      localFontFaces: branding.fonts ? getLocalFontFaces({ fonts: branding.fonts, locale }) : null,
    };
  }, [branding, locale]);

  const fullCssContent = useMemo(() => {
    const entries = Object.entries(cssVariables);
    if (entries.length === 0 && !localFontFaces) return null;

    const rules: string[] = [];

    if (localFontFaces) {
      rules.push(localFontFaces);
    }

    if (entries.length > 0) {
      const varsString = entries.map(([key, value]) => `${key}: ${value}`).join('; ');
      const fontBrand = cssVariables['--font-brand'];

      // CSS variables scoped to .bazak-branding + portal selectors (not :root, to avoid leaking into parent pages)
      rules.push(`${BRANDING_SELECTORS.join(', ')} { ${varsString}; }`);

      // Font-family scoped to branding container and portals to avoid leaking to parent page
      if (fontBrand) {
        const brandSelectors = BRANDING_SELECTORS.join(', ');
        const brandChildSelectors = BRANDING_SELECTORS.map(s => `${s} *`).join(', ');
        rules.push(`${brandSelectors} { font-family: ${fontBrand} !important; }`);
        rules.push(`${brandChildSelectors} { font-family: inherit !important; }`);

        // Reset to Geist font for debug/dev elements (reasoning, tool debug info, dev tools button)
        const defaultFontSelectors = DEFAULT_FONT_SELECTORS.flatMap(s => [s, `${s} *`]);
        rules.push(
          `${defaultFontSelectors.join(', ')} { font-family: var(--font-geist-sans), ui-sans-serif, system-ui, sans-serif !important; }`,
        );
      }
    }

    if (branding) {
      rules.push(...generateComponentOverrides(branding));
      rules.push(generateDarkModeOverrides(branding));
    }

    return rules.join('\n');
  }, [cssVariables, localFontFaces, branding]);

  // Render preconnect, font links, and styles inline for SSR - prevents FOUC
  return (
    <>
      {fontUrls.length > 0 && (
        <>
          <link rel="preconnect" href="https://fonts.googleapis.com" />
          <link rel="preconnect" href="https://fonts.gstatic.com" crossOrigin="anonymous" />
          {fontUrls.map(url => (
            <link key={url} rel="stylesheet" href={url} />
          ))}
        </>
      )}
      {fullCssContent && (
        <style
          id={BRANDING_STYLE_ID}
          suppressHydrationWarning
          dangerouslySetInnerHTML={{ __html: fullCssContent }}
        />
      )}
      {children}
    </>
  );
}
